/**
 * Order PDF OCR review step: editable PO/lines, optional quote picker when PO is not_found.
 */
import React, { useMemo, useState } from 'react';
import { logInfo, logWarn } from '../../config_and_helpers/logger';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { CheckCircle, ExclamationTriangle, Trash } from 'react-bootstrap-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NumberInput from '../Generic/NumberInput';
import { parseNumericInput } from '../../utils/numberInput';

const LOG_CONTEXT = 'OrderOcrReviewStep';
const LOGICAL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const MONTH_NAME_TO_NUMBER = {
  jan: 1,
  january: 1,
  ינואר: 1,
  ינו: 1,
  feb: 2,
  february: 2,
  פברואר: 2,
  פבר: 2,
  mar: 3,
  march: 3,
  מרץ: 3,
  מרס: 3,
  apr: 4,
  april: 4,
  אפריל: 4,
  אפר: 4,
  may: 5,
  מאי: 5,
  jun: 6,
  june: 6,
  יוני: 6,
  יונ: 6,
  jul: 7,
  july: 7,
  יולי: 7,
  יול: 7,
  aug: 8,
  august: 8,
  אוגוסט: 8,
  אוג: 8,
  sep: 9,
  sept: 9,
  september: 9,
  ספטמבר: 9,
  ספט: 9,
  oct: 10,
  october: 10,
  אוקטובר: 10,
  אוק: 10,
  nov: 11,
  november: 11,
  נובמבר: 11,
  נוב: 11,
  dec: 12,
  december: 12,
  דצמבר: 12,
  דצמ: 12,
  דצ: 12,
};

function pad2(v) {
  return String(v).padStart(2, '0');
}

function normalizeFlexibleDateToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return null;
  if (LOGICAL_DATE_REGEX.test(raw)) return raw;

  const m = raw.match(/^(\d{4})-([^-]+)-(\d{1,2})$/);
  if (!m) return null;

  const year = m[1];
  const monthRaw = String(m[2] || '').trim();
  const dayNum = Number(m[3]);
  if (!Number.isFinite(dayNum) || dayNum < 1 || dayNum > 31) return null;

  let monthNum = Number(monthRaw);
  if (!Number.isFinite(monthNum)) {
    const monthKey = monthRaw
      .toLowerCase()
      .replace(/[."'\u05F3\u05F4\u200F\u200E]/g, '')
      .trim();
    monthNum = MONTH_NAME_TO_NUMBER[monthKey];
  }
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return null;
  return `${year}-${pad2(monthNum)}-${pad2(dayNum)}`;
}

function isoToDisplayDate(iso) {
  const raw = String(iso || '').trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function normalizeDisplayDateToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return null;
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const day = Number(slash[1]);
    const month = Number(slash[2]);
    const year = slash[3];
    if (
      Number.isFinite(day) &&
      Number.isFinite(month) &&
      day >= 1 &&
      day <= 31 &&
      month >= 1 &&
      month <= 12
    ) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
    return null;
  }
  return normalizeFlexibleDateToken(raw);
}

function parseDisplayDateToDate(value) {
  const normalized = normalizeDisplayDateToken(value);
  if (!normalized) return null;
  const parts = normalized.split('-').map(v => Number(v));
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatDateToDisplay(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const day = pad2(value.getDate());
  const month = pad2(value.getMonth() + 1);
  const year = String(value.getFullYear());
  return `${day}/${month}/${year}`;
}

function normalizeDeliveryDatesForFields(values) {
  const source = Array.isArray(values) ? values : [];
  const mapped = source.map(v =>
    isoToDisplayDate(normalizeFlexibleDateToken(v) || String(v || ''))
  );
  const nonEmpty = mapped.filter(Boolean);
  return nonEmpty.length > 0 ? nonEmpty : [''];
}

function normalizePreview(preview) {
  return {
    supplier_parser_strategy: preview?.supplier_parser_strategy || '',
    po_number: preview?.po_number == null ? '' : String(preview.po_number),
    delivery_dates: Array.isArray(preview?.delivery_dates)
      ? preview.delivery_dates.map(v => String(v || '').trim()).filter(Boolean)
      : [],
    items: Array.isArray(preview?.items) ? preview.items : [],
    requires_manual_review: preview?.requires_manual_review === true,
    issues: Array.isArray(preview?.issues) ? preview.issues : [],
  };
}

/** @returns {unknown[]} Array rows from summary section or empty. */
function asRowArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatQty(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  const s = String(v).trim();
  return s || '—';
}

const CAT_CELL_KEYS = [
  'cat_num',
  'catalog',
  'catalog_num',
  'catalog_number',
  'sku',
  'item_code',
  'code',
];
const NOTES_KEYS = ['product_name', 'name', 'notes', 'issue', 'description'];

/** First non-empty value among `keys` on `obj` (supports nested product fields). */
function pickFirstDefined(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    const val = obj[k];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      return val;
    }
  }
  return undefined;
}

function pickCatalogCell(row) {
  if (!row || typeof row !== 'object') return '';
  let v = pickFirstDefined(row, CAT_CELL_KEYS);
  if (v !== undefined && v !== null) return String(v);
  const p = row.product;
  if (p && typeof p === 'object') {
    const nested = pickFirstDefined(p, ['catalog_num', 'cat_num', 'sku', 'code']);
    if (nested !== undefined) return String(nested);
  }
  return '';
}

function pickNotesCell(row) {
  if (!row || typeof row !== 'object') return '';
  const fromRoot = NOTES_KEYS.map(k => row[k]).filter(Boolean);
  const p = row.product;
  const fromProduct =
    typeof p === 'object' ? [p.product_name, p.name].filter(Boolean) : [];
  const parts = [...fromRoot, ...fromProduct].map(v => String(v).trim()).filter(Boolean);
  return parts.join(' · ');
}

const QUOTE_QTY_KEYS = ['quote_quantity', 'quote_qty', 'qty_on_quote', 'requested_quantity'];
const OCR_QTY_KEYS = ['ocr_quantity', 'extracted_quantity', 'pdf_quantity'];

function hasNonempty(val) {
  return val !== undefined && val !== null && String(val).trim() !== '';
}

/** Quote vs OCR quantity pair for table cells (dual-column mode uses both strings). */
function pickQuantityCells(row) {
  if (!row || typeof row !== 'object') {
    return { quoteQty: '', ocrQty: '', singleQty: '' };
  }

  let quoteQty = pickFirstDefined(row, QUOTE_QTY_KEYS);
  let ocrQty = pickFirstDefined(row, OCR_QTY_KEYS);
  const looseQty = row.quantity ?? row.qty;

  if (hasNonempty(quoteQty) && hasNonempty(ocrQty)) {
    return {
      quoteQty: formatQty(quoteQty),
      ocrQty: formatQty(ocrQty),
      singleQty: '',
    };
  }

  if (!hasNonempty(quoteQty) && !hasNonempty(ocrQty) && hasNonempty(looseQty)) {
    return { quoteQty: '', ocrQty: '', singleQty: formatQty(looseQty) };
  }

  if (hasNonempty(quoteQty) || hasNonempty(ocrQty)) {
    const only = hasNonempty(quoteQty) ? formatQty(quoteQty) : formatQty(ocrQty);
    return { quoteQty: '', ocrQty: '', singleQty: only };
  }

  return { quoteQty: '', ocrQty: '', singleQty: '' };
}

function rowHasDualQuantityRaw(row) {
  if (!row || typeof row !== 'object') return false;
  const q = pickFirstDefined(row, QUOTE_QTY_KEYS);
  const o = pickFirstDefined(row, OCR_QTY_KEYS);
  return hasNonempty(q) && hasNonempty(o);
}

/** Two quantity columns aligned to quote vs PDF (fills — when only one side is known). */
function pickQuotePdfQtyColumns(row) {
  if (!row || typeof row !== 'object') {
    return { left: '—', right: '—' };
  }
  const qRaw = pickFirstDefined(row, QUOTE_QTY_KEYS);
  const oRaw = pickFirstDefined(row, OCR_QTY_KEYS);
  const loose = row.quantity ?? row.qty;
  if (hasNonempty(qRaw) && hasNonempty(oRaw)) {
    return { left: formatQty(qRaw), right: formatQty(oRaw) };
  }
  if (hasNonempty(qRaw)) {
    return { left: formatQty(qRaw), right: '—' };
  }
  if (hasNonempty(oRaw)) {
    return { left: '—', right: formatQty(oRaw) };
  }
  if (hasNonempty(loose)) {
    const q = formatQty(loose);
    return { left: q, right: q };
  }
  return { left: '—', right: '—' };
}

function formatPrimitiveCell(cell) {
  if (cell == null) return '—';
  const t = typeof cell;
  if (t === 'string' || t === 'number' || t === 'boolean') return String(cell);
  try {
    return JSON.stringify(cell);
  } catch {
    return '—';
  }
}

const CAT_MATCH_SECTIONS = [
  {
    key: 'matched',
    title: 'Matches your PDF',
    hint: 'Lines on this quote that line up with the extracted PDF.',
  },
  {
    key: 'missing_from_extraction',
    title: 'On quote only',
    hint: 'On the quote but not found in what we extracted.',
  },
  {
    key: 'unmatched_extracted',
    title: 'In PDF only',
    hint: 'In the extraction but not on this quote.',
  },
];

/**
 * Renders `cat_match_summary` as compact operator-focused tables (+ optional JSON for support).
 * @param {Object} props
 * @param {Object|null} [props.summary] - Backend `cat_match_summary` object
 */
function QuoteCatMatchSummary({ summary }) {
  if (!summary || typeof summary !== 'object') {
    return (
      <p className="text-muted small mb-0">No detailed line breakdown from server.</p>
    );
  }

  let anyRows = false;
  for (const { key } of CAT_MATCH_SECTIONS) {
    if (asRowArray(summary[key]).length > 0) {
      anyRows = true;
      break;
    }
  }

  if (!anyRows) {
    return (
      <>
        <p className="text-muted small mb-2">No detailed line breakdown from server.</p>
        <details className="small">
          <summary className="text-muted">Technical detail (support)</summary>
          <pre
            className="mt-1 mb-0 p-2 bg-light border rounded small"
            style={{ maxHeight: 160, overflow: 'auto' }}
          >
            {JSON.stringify(summary, null, 2)}
          </pre>
        </details>
      </>
    );
  }

  const blocks = CAT_MATCH_SECTIONS.map(({ key, title, hint }) => {
    const rawRows = asRowArray(summary[key]);
    if (rawRows.length === 0) return null;

    const objectRows = rawRows.filter(
      r => r !== null && typeof r === 'object' && !Array.isArray(r)
    );
    const primitiveRows = rawRows.filter(
      r => r === null || typeof r !== 'object' || Array.isArray(r)
    );

    const primitiveFragment =
      primitiveRows.length > 0 ? (
        <Table striped bordered size="sm" responsive className="mb-2 bg-white small">
          <thead className="table-light">
            <tr>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {primitiveRows.map((cell, i) => (
              <tr key={`${key}-prim-${i}`}>
                <td className="text-break">{formatPrimitiveCell(cell)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null;

    if (objectRows.length === 0) {
      return (
        <div className="mb-3" key={key}>
          <div className="fw-semibold small">{title}</div>
          <div className="text-muted small mb-1">{hint}</div>
          {primitiveFragment}
        </div>
      );
    }

    const showCatalog = objectRows.some(r => pickCatalogCell(r) !== '');
    const showDualQty = objectRows.some(r => rowHasDualQuantityRaw(r));
    const showSingleQty =
      !showDualQty &&
      objectRows.some(r => {
        const { singleQty } = pickQuantityCells(r);
        return singleQty !== '' && singleQty !== '—';
      });
    const needsNotesColumn = objectRows.some(r => pickNotesCell(r) !== '');

    const colCount =
      (showCatalog ? 1 : 0) +
      (showDualQty ? 2 : showSingleQty ? 1 : 0) +
      (needsNotesColumn ? 1 : 0);

    const objectBody =
      colCount === 0 ? (
        <Table striped bordered size="sm" responsive className="mb-0 bg-white small">
          <thead className="table-light">
            <tr>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {objectRows.map((row, i) => (
              <tr key={`${key}-fallback-${i}`}>
                <td className="text-break small font-monospace">
                  {(() => {
                    try {
                      return JSON.stringify(row);
                    } catch {
                      return '—';
                    }
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Table striped bordered size="sm" responsive className="mb-0 bg-white small">
          <thead className="table-light">
            <tr>
              {showCatalog && <th>Catalog</th>}
              {showDualQty && (
                <>
                  <th>Qty (quote)</th>
                  <th>Qty (PDF)</th>
                </>
              )}
              {!showDualQty && showSingleQty && <th>Qty</th>}
              {needsNotesColumn && <th>Notes</th>}
            </tr>
          </thead>
          <tbody>
            {objectRows.map((row, i) => {
              const qtyPair = pickQuotePdfQtyColumns(row);
              const { singleQty } = pickQuantityCells(row);
              const cat = pickCatalogCell(row);
              const notes = pickNotesCell(row);
              return (
                <tr key={`${key}-obj-${i}`}>
                  {showCatalog && <td>{cat !== '' ? cat : '—'}</td>}
                  {showDualQty && (
                    <>
                      <td>{qtyPair.left}</td>
                      <td>{qtyPair.right}</td>
                    </>
                  )}
                  {!showDualQty && showSingleQty && (
                    <td>{singleQty !== '' ? singleQty : '—'}</td>
                  )}
                  {needsNotesColumn && (
                    <td className="text-break">{notes !== '' ? notes : '—'}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      );

    return (
      <div className="mb-3" key={key}>
        <div className="fw-semibold small">{title}</div>
        <div className="text-muted small mb-1">{hint}</div>
        {primitiveFragment}
        {objectBody}
      </div>
    );
  });

  return (
    <div className="mt-2">
      {blocks}
      <details className="small mt-1">
        <summary className="text-muted">Technical detail (support)</summary>
        <pre
          className="mt-1 mb-0 p-2 bg-light border rounded small"
          style={{ maxHeight: 160, overflow: 'auto' }}
        >
          {JSON.stringify(summary, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * Order PDF OCR review: editable PO/lines and optional ranked quote pick when PO is `not_found`.
 *
 * @param {Object} props
 * @param {Object} props.reviewRow - Merged candidate from upload (`order_lookup_status`, `quote_candidates`, …).
 * @param {boolean} [props.isSubmitting]
 * @param {string} [props.submitError] - Primary submit error message.
 * @param {Object|null} [props.submitFailureDetails] - API `review_errors` / `post_extraction_review` after failed submit.
 * @param {number|null} [props.selectedQuoteId] - Chosen quote PK for create path.
 * @param {string} [props.defaultReceivedBy] - Prefill display name for `received_by` (PO found / create-from-quote).
 * @param {function(number): void} [props.onQuoteSelectionChange]
 * @param {function(Object): void} props.onSubmit - Receives JSON including optional `selected_quote_id`.
 * @param {function(): void} props.onBack
 */
const OrderOcrReviewStep = ({
  reviewRow,
  defaultReceivedBy = '',
  isSubmitting = false,
  submitError = '',
  submitFailureDetails = null,
  selectedQuoteId = null,
  onQuoteSelectionChange,
  onBack,
  onSubmit,
}) => {
  const initialPreview = useMemo(
    () => normalizePreview(reviewRow?.ocr_extraction_preview || {}),
    [reviewRow]
  );

  const [poNumber, setPoNumber] = useState(initialPreview.po_number);
  const [deliveryDates, setDeliveryDates] = useState(
    normalizeDeliveryDatesForFields(initialPreview.delivery_dates)
  );
  const [rows, setRows] = useState(
    (initialPreview.items || []).map((item, i) => ({
      line_id: i + 1,
      cat_num: item?.cat_num == null ? '' : String(item.cat_num),
      product_name:
        item?.product_name == null ? '' : String(item.product_name),
      quantity: item?.quantity == null ? '' : String(item.quantity),
      batch_no: item?.batch_no == null ? '' : String(item.batch_no),
      expiry: item?.expiry == null ? '' : String(item.expiry),
      issues: Array.isArray(item?.issues) ? item.issues : [],
      removed: false,
    }))
  );
  const [validationError, setValidationError] = useState('');
  const [receivedBy, setReceivedBy] = useState(() =>
    String(defaultReceivedBy || '').trim()
  );

  const editableRows = useMemo(() => rows.filter(r => !r.removed), [rows]);

  const lookupStatus = String(reviewRow?.order_lookup_status || '').toLowerCase();
  const suggestQuotes = reviewRow?.creation_mode === 'suggest_quotes';
  const quoteCandidates = useMemo(
    () =>
      Array.isArray(reviewRow?.quote_candidates)
        ? reviewRow.quote_candidates.slice(0, 3)
        : [],
    [reviewRow?.quote_candidates]
  );

  const needsQuoteSelection =
    lookupStatus === 'not_found' && suggestQuotes && quoteCandidates.length > 0;
  const blockedCreateNoCandidates =
    lookupStatus === 'not_found' && suggestQuotes && quoteCandidates.length === 0;
  /** PO not in DB and no quote picker path → cannot satisfy submit_reviewed_ocr create rules here. */
  const blockedNotFoundNoPicker =
    lookupStatus === 'not_found' && !(suggestQuotes && quoteCandidates.length > 0);

  const lookupBadgeVariant = st => {
    if (st === 'found') return 'success';
    if (st === 'not_found') return 'warning';
    if (st === 'invalid') return 'danger';
    return 'secondary';
  };

  const setRow = (index, patch) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const validate = () => {
    if (!String(receivedBy || '').trim()) return 'Received by is required.';
    if (!String(poNumber || '').trim()) return 'PO number is required.';
    if (editableRows.length === 0) {
      return 'At least one non-removed item is required.';
    }
    for (const dRaw of deliveryDates) {
      const d = String(dRaw || '').trim();
      if (!d) continue;
      const normalized = normalizeDisplayDateToken(d);
      if (!normalized || !LOGICAL_DATE_REGEX.test(normalized)) {
        return `Delivery date "${d}" must be in DD/MM/YYYY format.`;
      }
    }
    for (let i = 0; i < editableRows.length; i += 1) {
      const row = editableRows[i];
      if (!String(row.cat_num || '').trim()) {
        return `Item ${i + 1}: cat_num is required.`;
      }
      if (!String(row.quantity || '').trim()) {
        return `Item ${i + 1}: quantity is required.`;
      }
      const q = parseNumericInput(row.quantity, { integerOnly: true });
      if (q === null || q < 0) {
        return `Item ${i + 1}: quantity must be a non-negative number.`;
      }
      const expiry = String(row.expiry || '').trim();
      if (expiry && !LOGICAL_DATE_REGEX.test(expiry)) {
        return `Item ${i + 1}: expiry must be in YYYY-MM-DD format when provided.`;
      }
    }
    return '';
  };

  const receivedByMissing = !String(receivedBy || '').trim();

  const isSubmitActionDisabled =
    isSubmitting ||
    receivedByMissing ||
    blockedCreateNoCandidates ||
    blockedNotFoundNoPicker ||
    (needsQuoteSelection &&
      (selectedQuoteId == null ||
        selectedQuoteId === '' ||
        !Number.isFinite(Number(selectedQuoteId))));

  const handleSubmit = () => {
    const err = validate();
    if (err) {
      setValidationError(err);
      logWarn(LOG_CONTEXT, 'client validation failed', {
        reason: err,
        lookupStatus,
      });
      return;
    }
    if (needsQuoteSelection) {
      const qid = Number(selectedQuoteId);
      if (!Number.isFinite(qid)) {
        setValidationError('Select a quote before submitting.');
        return;
      }
    }
    setValidationError('');
    const payload = {
      received_by: String(receivedBy || '').trim(),
      ocr_extraction_preview: {
        supplier_parser_strategy: initialPreview.supplier_parser_strategy || '',
        po_number: String(poNumber || '').trim(),
        delivery_dates: deliveryDates
          .map(v => normalizeDisplayDateToken(v))
          .filter(Boolean),
        requires_manual_review: initialPreview.requires_manual_review === true,
        issues: Array.isArray(initialPreview.issues)
          ? initialPreview.issues
          : [],
        items: editableRows.map(r => ({
          cat_num: String(r.cat_num || '').trim(),
          product_name: String(r.product_name || '').trim(),
          quantity:
            parseNumericInput(r.quantity, { integerOnly: true }) ??
            Number(String(r.quantity || '').trim()),
          batch_no: String(r.batch_no || '').trim(),
          expiry: String(r.expiry || '').trim(),
          issues: Array.isArray(r.issues) ? r.issues : [],
        })),
      },
    };

    if (needsQuoteSelection && selectedQuoteId != null) {
      payload.selected_quote_id = Number(selectedQuoteId);
    }

    logInfo(LOG_CONTEXT, 'submit reviewed OCR payload', {
      pdfName: reviewRow?.pdf_name ?? null,
      fileIndex: reviewRow?.file_index ?? null,
      lookupStatus,
      creationMode: reviewRow?.creation_mode ?? null,
      itemCount: editableRows.length,
      needsQuoteSelection,
      selectedQuoteId: payload.selected_quote_id ?? null,
    });

    onSubmit(payload);
  };

  const pickGroupName = `order-ocr-quote-${reviewRow?.key ?? 'file'}`;

  return (
    <div className="mt-3 border rounded p-3 bg-white">
      <h6 className="d-flex align-items-center gap-2 mb-3">
        <CheckCircle size={16} className="text-primary" />
        Review OCR extraction before order upsert
      </h6>

      <div className="small text-muted mb-2 d-flex flex-wrap align-items-center gap-2">
        <span>
          File: {reviewRow?.pdf_name || `File ${reviewRow?.file_index ?? '—'}`}
          {reviewRow?.source_mode ? ` | source: ${reviewRow.source_mode}` : ''}
        </span>
        {reviewRow?.order_lookup_status && (
          <Badge bg={lookupBadgeVariant(lookupStatus)}>
            PO lookup: {reviewRow.order_lookup_status}
          </Badge>
        )}
        {reviewRow?.creation_mode != null && reviewRow?.creation_mode !== '' && (
          <Badge bg="info" text="dark">
            {String(reviewRow.creation_mode)}
          </Badge>
        )}
      </div>

      {(reviewRow?.review_errors || []).length > 0 && (
        <Alert variant="warning" className="small py-2">
          <div className="d-flex align-items-start gap-2">
            <ExclamationTriangle className="mt-1" />
            <div>
              {(reviewRow.review_errors || []).map((issue, i) => (
                <div key={`review-issue-${i}`}>{String(issue)}</div>
              ))}
            </div>
          </div>
        </Alert>
      )}

      {lookupStatus === 'invalid' && (
        <Alert variant="danger" className="small py-2">
          <strong>Invalid PO / resolver state.</strong> Fix PO or lines below; the server may still
          reject until rules pass.
        </Alert>
      )}

      {blockedCreateNoCandidates && (
        <Alert variant="warning" className="small py-2">
          No eligible quote suggestions for this extraction (overlap may be empty or every matching
          quote already has an order). Correct the PO or catalog lines, or create or link an order
          outside this flow.
        </Alert>
      )}

      {blockedNotFoundNoPicker && !blockedCreateNoCandidates && (
        <Alert variant="warning" className="small py-2">
          This PO is not in the system and no quote suggestions are available for selection. Adjust
          the PO or extraction, or create or link an order outside this flow — submit is disabled
          until a quote can be chosen.
        </Alert>
      )}

      {needsQuoteSelection && (
        <div className="mb-3">
          <h6 className="small fw-semibold mb-2">Select a quote to create an order</h6>
          <Row className="g-3">
            {quoteCandidates.map((q, qi) => {
              const id = q?.quote_id;
              const selected = Number(selectedQuoteId) === Number(id);
              const supplierName = q?.supplier?.name || '—';
              const n = quoteCandidates.length;
              const summary =
                q?.cat_match_summary && typeof q.cat_match_summary === 'object'
                  ? q.cat_match_summary
                  : null;
              return (
                <Col key={`qc-${id}`} xs={12}>
                  <Card
                    className={selected ? 'border-primary border-2' : ''}
                    style={{ cursor: 'pointer' }}
                    role="button"
                    tabIndex={0}
                    onClick={() => onQuoteSelectionChange && onQuoteSelectionChange(Number(id))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onQuoteSelectionChange && onQuoteSelectionChange(Number(id));
                      }
                    }}
                  >
                    <Card.Body className="py-3 px-3 small">
                      <Form.Check
                        type="radio"
                        className="mb-2"
                        name={pickGroupName}
                        id={`${pickGroupName}-${id}`}
                        checked={selected}
                        onChange={() =>
                          onQuoteSelectionChange && onQuoteSelectionChange(Number(id))
                        }
                        label={
                          <span className="fw-semibold">
                            Suggestion {qi + 1} of {n}
                          </span>
                        }
                      />
                      <div className="text-muted mb-2">Quote #{id}</div>
                      <div className="text-muted">Corporate demand reference</div>
                      <div className="text-break mb-2">
                        {q?.corporate_demand_ref != null && q.corporate_demand_ref !== ''
                          ? String(q.corporate_demand_ref)
                          : '—'}
                      </div>
                      <div className="text-muted">Supplier</div>
                      <div className="mb-3 text-break">{supplierName}</div>

                      <div className="fw-semibold small mb-2">Line comparison</div>
                      <QuoteCatMatchSummary summary={summary} />
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
          {needsQuoteSelection &&
            (selectedQuoteId == null || selectedQuoteId === '') && (
              <div className="small text-danger mt-1">Choose one quote to submit.</div>
            )}
        </div>
      )}

      <Row className="g-3 mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Received by</Form.Label>
            <Form.Control
              value={receivedBy}
              onChange={e => setReceivedBy(e.target.value)}
              placeholder="Who received this shipment"
              disabled={isSubmitting}
              autoComplete="name"
            />
            <Form.Text className="text-muted">
              Recorded with the order when you submit this review (existing PO or new order from
              quote).
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>PO number</Form.Label>
            <Form.Control
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Delivery dates</Form.Label>
            <div className="d-flex flex-column gap-2">
              {deliveryDates.map((dateValue, index) => (
                <div key={`delivery-date-${index}`} className="d-flex gap-2">
                  <DatePicker
                    selected={parseDisplayDateToDate(dateValue)}
                    onChange={selectedDate => {
                      setDeliveryDates(prev =>
                        prev.map((v, i) =>
                          i === index
                            ? selectedDate
                              ? formatDateToDisplay(selectedDate)
                              : ''
                            : v
                        )
                      );
                    }}
                    onChangeRaw={event =>
                      setDeliveryDates(prev =>
                        prev.map((v, i) =>
                          i === index ? event?.target?.value || '' : v
                        )
                      )
                    }
                    dateFormat="dd/MM/yyyy"
                    placeholderText="dd/mm/yyyy"
                    className="form-control me-2"
                    wrapperClassName="me-2 flex-grow-1"
                    inputClassName="text-center"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    todayButton="Today"
                    disabled={isSubmitting}
                  />
                  <Button
                    variant="outline-danger"
                    onClick={() =>
                      setDeliveryDates(prev =>
                        prev.length > 1
                          ? prev.filter((_, i) => i !== index)
                          : ['']
                      )
                    }
                    disabled={isSubmitting}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <div>
                <Button
                  variant="outline-primary"
                  onClick={() => setDeliveryDates(prev => [...prev, ''])}
                  disabled={isSubmitting}
                >
                  Add delivery date
                </Button>
              </div>
            </div>
          </Form.Group>
        </Col>
      </Row>

      <div className="mb-2 d-flex align-items-center gap-2">
        <strong>Items</strong>
        <Badge bg="secondary">{editableRows.length} active</Badge>
      </div>
      <Table striped bordered hover responsive size="sm" className="small bg-white">
        <thead>
          <tr>
            <th>#</th>
            <th>Cat #</th>
            <th>Product name</th>
            <th>Quantity</th>
            <th>Batch no</th>
            <th>Expiry</th>
            <th>Issues</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`order-ocr-row-${row.line_id}-${idx}`}>
              <td>{row.line_id}</td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.cat_num}
                  onChange={e => setRow(idx, { cat_num: e.target.value })}
                  disabled={isSubmitting || row.removed}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.product_name}
                  onChange={e => setRow(idx, { product_name: e.target.value })}
                  disabled={isSubmitting || row.removed}
                />
              </td>
              <td>
                <NumberInput
                  name={`orderOcrQty-${idx}`}
                  integerOnly
                  size="sm"
                  value={row.quantity}
                  onChange={e => setRow(idx, { quantity: e.target.value })}
                  disabled={isSubmitting || row.removed}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  value={row.batch_no}
                  onChange={e => setRow(idx, { batch_no: e.target.value })}
                  disabled={isSubmitting || row.removed}
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  placeholder="YYYY-MM-DD"
                  value={row.expiry}
                  onChange={e => setRow(idx, { expiry: e.target.value })}
                  disabled={isSubmitting || row.removed}
                />
              </td>
              <td>
                {(row.issues || []).map((issue, i) => (
                  <Badge key={`row-issue-${idx}-${i}`} bg="light" text="dark" className="me-1">
                    {String(issue)}
                  </Badge>
                ))}
              </td>
              <td>
                <Button
                  size="sm"
                  variant={row.removed ? 'outline-secondary' : 'outline-danger'}
                  onClick={() => setRow(idx, { removed: !row.removed })}
                  disabled={isSubmitting}
                >
                  <Trash size={12} className="me-1" />
                  {row.removed ? 'Restore' : 'Remove'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {validationError && (
        <Alert variant="danger" className="small py-2">
          {validationError}
        </Alert>
      )}
      {submitError && (
        <Alert variant="danger" className="small py-2">
          {submitError}
        </Alert>
      )}

      {submitFailureDetails &&
        (() => {
          const extraErrors = Array.isArray(submitFailureDetails.review_errors)
            ? submitFailureDetails.review_errors
            : [];
          const hasPost = submitFailureDetails.post_extraction_review != null;
          if (!extraErrors.length && !hasPost) return null;
          return (
            <Alert variant="danger" className="small py-2">
              {extraErrors.length > 0 && (
                <div className="mb-2">
                  <div className="fw-semibold mb-1">Server review messages</div>
                  {extraErrors.map((line, i) => (
                    <div key={`sf-${i}`}>{String(line)}</div>
                  ))}
                </div>
              )}
              {hasPost && (
                <details className="mb-0">
                  <summary className="text-muted">post_extraction_review</summary>
                  <pre
                    className="mt-2 mb-0 p-2 bg-light border rounded small overflow-auto"
                    style={{ maxHeight: 220 }}
                  >
                    {JSON.stringify(submitFailureDetails.post_extraction_review, null, 2)}
                  </pre>
                </details>
              )}
            </Alert>
          );
        })()}

      <div className="d-flex gap-2 mt-2">
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={isSubmitActionDisabled}
        >
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Submitting...
            </>
          ) : (
            'Submit reviewed OCR'
          )}
        </Button>
        <Button variant="outline-secondary" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
      </div>
    </div>
  );
};

export default OrderOcrReviewStep;
